import React from 'react'
import { Reservation } from 'src/services/reservations'
import { reservationApi } from 'src/services/reservations/api'
import { reservationQueries } from 'src/services/reservations/queries'
import { Button } from 'src/components/button'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Gate } from 'src/components/gate'

interface ReservationCardActionWrapperProps {
  reservation: Reservation
  children: React.ReactNode
}

function getReservationDisplayName(reservation: Reservation): string {
  const firstGuest = reservation.reservationGuestsRef?.[0]?.guestRef
  if (firstGuest) {
    return `${firstGuest.firstName} ${firstGuest.lastName}`.trim()
  }
  return reservation.providerConfirmationCode || reservation.id
}

export const ReservationCardActionWrapper: React.FC<ReservationCardActionWrapperProps> = ({
  reservation,
  children,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: reservationApi.remove,
    queryKey: reservationQueries.all(),
  })

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    requestDelete(
      reservation.id,
      reservation.kind === 'block'
        ? t('reservations.blocked')
        : getReservationDisplayName(reservation),
    )
  }

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/reservations/${reservation.id}`)
  }

  return (
    <>
      <ConfirmDeleteDialog {...dialogProps} />
      <div className="group relative">
        {children}

        {/* Action buttons - show on hover */}
        <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
          <Gate permission="reservations.edit">
            <Button
              onClick={handleEdit}
              variant="secondary"
              iconOnly
              shape="circle"
              className="shadow-md"
              data-testid="edit-reservation-button"
              title={t('reservations.editReservation')}
            >
              <PencilIcon className="h-4 w-4 text-accent-600" />
            </Button>
          </Gate>
          <Gate permission="reservations.delete">
            <Button
              onClick={handleDelete}
              disabled={dialogProps.isPending}
              variant="secondary"
              iconOnly
              shape="circle"
              className="shadow-md"
              data-testid="delete-reservation-button"
              title={t('reservations.deleteReservation')}
            >
              {dialogProps.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-error-300 border-t-error" />
              ) : (
                <TrashIcon className="h-4 w-4 text-error-600" />
              )}
            </Button>
          </Gate>
        </div>
      </div>
    </>
  )
}
